import React, { useState, useEffect, useMemo, useRef } from "react";
import { Checkbox, Button, Switch, DatePicker, Row, Col, Input, Tooltip } from "antd";
import dayjs from "dayjs";
import { NotificationOutlined, SearchOutlined, WarningOutlined } from "@ant-design/icons";


// Import api info
import { createAuthSamples, activatePatientAuthSamples, deactivatePatientAuthSamples, updateAuthSampleDeactivationDate, fetchPatientAuthSamples } from "../../../../api/geneCodes/AuthSamples";
import { getSamplesPanels, fetchPanels, createOrUpdateSamplesPanels } from "../../../../api/geneCodes/PanelApi";
import { Panel, PanelStatus } from "../../../../types/geneCodes/Panel";
import { HIDDEN_WHEEL_ASSIGNED_NOTE, panelStatusOf } from "../../../../utils/geneCodes/wheelAuthoring";
import { findAuthSample, isAuthSampleActive } from "../../../../utils/geneCodes/authSampleAccess";
import { assignGeneCodes } from "../../../../api/healthcare/PatientApi";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";

//Types
import { Patient } from "../../../../types/healthcare/PatientTypes";

import { useNotifications } from "../../../../context/NotificationContext";
import { extractValidationErrors } from "../../../../utils/apiError";
import { sendCommunication } from "../../../../api/communications/communicationApi";
import type { SendCommunicationRequest } from "../../../../types/communications/CommunicationTypes";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import StatusTag from "../../../../components/shared/StatusTag";
// Direct paths: components/shared and components/charts are barrels that pull in d3, which ts-jest cannot transform.
import { MiniDonut, MiniDonutSegment } from "../../../../components/charts/MiniDonut";
import { buildPanelMiniWheels, panelKey } from "../../../../utils/geneCodes/buildPanelMiniWheels";
import { getNodeColor } from "../../../../utils/geneCodes/wheelColors";
import { usePanelRiskSummary } from "../../../../hooks/genetic/usePanelRiskSummary";

interface GeneCodesTabProps {
	patient: Patient;
	clientSampleId: string;
}

// sample_activation is unknown because labiq serves it as a number, a boolean or a string, depending on the engine.
type AuthSampleRow = { sampleID: string; idauth_samples: number; sample_activation: unknown; sample_deactivation_date: string | null };

/**
 * `CommunicationController::send()` answers 422 with this exact string when the recipient has
 * every notification channel switched off. That is the patient exercising a preference, not the
 * system breaking, and the practitioner has to be able to tell the two apart.
 */
const ALL_CHANNELS_DISABLED_ERROR = "User has all notification channels disabled";

const isAllChannelsDisabled = (error: any): boolean => error?.response?.status === 422 && error?.response?.data?.error === ALL_CHANNELS_DISABLED_ERROR;

const SAVE_DEBOUNCE_MS = 600;
const MINI_WHEEL_SIZE = 32;

const isAssignable = (panel: Panel): boolean => panelStatusOf(panel) === "published" || panelStatusOf(panel) === "hidden";

const STATUS_TAG: Record<PanelStatus, { label: string; color: string; tooltip?: string }> = {
	published: { label: "Published", color: "var(--mg-success)" },
	hidden: { label: "Hidden", color: "var(--mg-text-secondary)", tooltip: HIDDEN_WHEEL_ASSIGNED_NOTE },
	draft: { label: "Draft", color: "var(--mg-warning)" },
	archived: { label: "Archived", color: "var(--mg-text-secondary)" },
};

const sortIds = (ids: number[]): number[] => [...ids].sort((a, b) => a - b);

const sameIds = (a: number[], b: number[]): boolean => a.length === b.length && a.every((id, index) => id === b[index]);


const buildTemplateData = (patient: Patient) => ({
	first_name: patient.first_name,
	middle_name: patient.middle_name,
	last_name: patient.last_name,
	username: patient.username,
	email: patient.email,
	phone: patient.phone,
});

const GeneCodesTab: React.FC<GeneCodesTabProps> = ({ patient, clientSampleId }) => {
	const [authSampleId, setAuthSampleId] = useState<number>(0);
	const [panelList, setPanelList] = useState<Panel[]>([]);
	const [isActive, setIsActive] = useState<boolean>(false);
	const [selectedPanels, setSelectedPanels] = useState<number[]>([]);
	const [deactivationDate, setDeactivationDate] = useState<dayjs.Dayjs | null>(null);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [sendingNotification, setSendingNotification] = useState(false);
	const [search, setSearch] = useState("");

	// What the server is known to hold, so a load never writes itself back and an unchanged set never writes at all.
	const savedPanelsRef = useRef<number[]>([]);

	const dateFormat = loadUserPreferences().dateFormat.toUpperCase();

	const { showSuccess, showError } = useNotifications();

	const { rows } = usePanelRiskSummary(clientSampleId);
	const wheels = useMemo(() => buildPanelMiniWheels(rows), [rows]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const { data } = await fetchPanels(undefined, undefined, undefined, true, "assignable");
				setPanelList(data);
				await createAuthSamples(patient.email, clientSampleId);
				let authId = 0;
				let smpls = await fetchPatientAuthSamples(patient.email);
				const matchingSample = findAuthSample<AuthSampleRow>(smpls, clientSampleId);
				if (matchingSample) {
					setIsActive(isAuthSampleActive(matchingSample.sample_activation));
					setAuthSampleId(matchingSample.idauth_samples);
					setDeactivationDate(matchingSample.sample_deactivation_date ? dayjs(matchingSample.sample_deactivation_date) : null);
					authId = matchingSample.idauth_samples;
				}

				const samplePanels = await getSamplesPanels(authId);
				const loaded = (samplePanels ?? []).flat();
				setSelectedPanels(loaded);

				const blocked = new Set(data.filter((panel) => !isAssignable(panel)).map((panel) => panel.id_panels));
				savedPanelsRef.current = sortIds(loaded.filter((id) => !blocked.has(id)));

				setIsInitialLoad(false);
			} catch (error) {
				console.error("Error fetching data:", error);
			}
		};

		fetchData();
		// authSampleId is only WRITTEN here — listing it re-ran the whole loader, and createAuthSamples, twice per mount.
	}, [clientSampleId, patient.email]);

	const activationOfSample = async (setActive: boolean) => {
		try {
			if (setActive) {
				await activatePatientAuthSamples(authSampleId);
				
				// Automatically set deactivation date to 3 months from now
				const threeMonthsFromNow = dayjs().add(3, 'month');
				setDeactivationDate(threeMonthsFromNow);
				await updateAuthSampleDeactivationDate(authSampleId, threeMonthsFromNow);
			} else {
				await deactivatePatientAuthSamples(authSampleId);
			}
			setIsActive(setActive);
		} catch (error) {
			console.error("Error activating sample:", error);
		}
	};

	const updateDeactivationDate = async (date: dayjs.Dayjs | null) => {
		if (date) {
			try {
				setDeactivationDate(date);
				await updateAuthSampleDeactivationDate(authSampleId, date);
			} catch (error) {
				console.error("Failed to update deactivation date:", error);
			}
		}
	};

	const assignablePanels = useMemo(() => panelList.filter(isAssignable), [panelList]);

	const unassignableIds = useMemo(() => new Set(panelList.filter((panel) => !isAssignable(panel)).map((panel) => panel.id_panels)), [panelList]);

	// Only an id we can SEE is a draft is stripped; one we know nothing about is echoed back untouched.
	const panelsToSave = useMemo(() => sortIds(selectedPanels.filter((id) => !unassignableIds.has(id))), [selectedPanels, unassignableIds]);

	useEffect(() => {
		if (isInitialLoad || !authSampleId) return;
		if (sameIds(panelsToSave, savedPanelsRef.current)) return;

		// One write per burst — this used to PUT the whole set on every single checkbox tick.
		const timer = setTimeout(() => {
			createOrUpdateSamplesPanels(authSampleId, panelsToSave)
				.then(() => {
					savedPanelsRef.current = panelsToSave;
				})
				.catch((error) => {
					showError(extractValidationErrors(error).join(' '));
					setSelectedPanels(savedPanelsRef.current);
				});
		}, SAVE_DEBOUNCE_MS);

		return () => clearTimeout(timer);
	}, [panelsToSave, authSampleId, isInitialLoad, showError]);

	const handleCheckboxChange = (panelId: number, checked: boolean) => {
		setSelectedPanels((prev) => (checked ? [...prev, panelId] : prev.filter((id) => id !== panelId)));
	};

	const allAssignableSelected = assignablePanels.length > 0 && assignablePanels.every((panel) => selectedPanels.includes(panel.id_panels));

	const handleSelectAll = () => {
		setSelectedPanels(allAssignableSelected ? [] : assignablePanels.map((panel) => panel.id_panels));
	};

	const visiblePanels = useMemo(() => {
		const term = search.trim().toLowerCase();
		if (!term) return panelList;
		return panelList.filter((panel) => panel.name_panel.toLowerCase().includes(term) || (panel.modality ?? "").toLowerCase().includes(term));
	}, [panelList, search]);

	const sampleStatus = async (isActive: boolean) => {
		if (isActive) {
			await assignGeneCodes(patient.id);
			activatePatientAuthSamples(authSampleId);
		} else {
			deactivatePatientAuthSamples(authSampleId);
		}
	};

	const handleSendNotification = async (reportType: string) => {
			setSendingNotification(true);
			try {
				const user = getSessionUser();
				const payload: SendCommunicationRequest = {
					// The auth-service user id, which is what `sent_notifications.recipient_user_id`
					// stores. `recipient_email` / `recipient_phone` / `company_id` are deliberately
					// absent: the live endpoint validates none of them and resolves contact details
					// server-side from this id.
					recipient_user_id: patient.id,
					category: reportType,
					// `channels` is deliberately NOT sent. The template's own type decides, and the
					// patient's stored notification preferences override that.
					// An explicit, enumerated allowlist — never a patient spread. See buildTemplateData.
					data: buildTemplateData(patient),
					user_id: user.id,
				};
				await sendCommunication(payload);
				showSuccess("Notification has been sent to the patient");
			} catch (error) {
				console.error("Failed to send notification:", error);
				if (isAllChannelsDisabled(error)) {
					showError("This patient has turned off every notification channel, so nothing was sent. Ask them to re-enable email or SMS in their notification preferences.");
				} else {
					showError("Failed to send notification. Please try again.");
				}
			} finally {
				setSendingNotification(false);
			}

		};

	const renderMiniWheel = (panel: Panel) => {
		const slices = wheels.get(panelKey(panel.name_panel));
		if (!slices || slices.length === 0) {
			return <div style={{ width: MINI_WHEEL_SIZE, height: MINI_WHEEL_SIZE, flex: `0 0 ${MINI_WHEEL_SIZE}px` }} />;
		}

		const segments: MiniDonutSegment[] = slices.map((slice) => ({
			name: slice.trait,
			value: slice.value,
			color: getNodeColor({ id: `trait-${slice.trait}`, name: slice.trait, type: "trait", value: slice.value, data: {}, riskLevel: slice.riskLevel }, false),
		}));

		return <MiniDonut segments={segments} size={MINI_WHEEL_SIZE} title={panel.name_panel} plate="var(--mg-bg-subtle)" />;
	};

	const panelMeta = (panel: Panel): string =>
		[panel.trait_count === undefined ? null : `${panel.trait_count} traits`, panel.snp_count === undefined ? null : `${panel.snp_count} SNPs`, panel.modality].filter(Boolean).join(" · ");

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			<div style={{ padding: "12px 16px", border: "1px solid var(--mg-border)", borderRadius: "var(--mg-radius-lg)", background: "var(--mg-bg-card)" }}>
				<Row gutter={[16, 16]}>
					<Col>
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<label style={{ fontWeight: 500 }}>Activation Status</label>
							<Switch
								checked={isActive}
								onChange={(checked) => {
									activationOfSample(checked);
									sampleStatus(checked);
								}}
								checkedChildren="Active"
								unCheckedChildren="Inactive"
							/>
						</div>
					</Col>

					<Col>
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<label style={{ fontWeight: 500 }}>Deactivation Date</label>
							<DatePicker value={deactivationDate} onChange={updateDeactivationDate} format={dateFormat} style={{ width: 200 }} />
						</div>
					</Col>

					<Col>
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<Button icon={<NotificationOutlined />} onClick={() => handleSendNotification("gene_codes_available_notification")} loading={sendingNotification}>
								Send Access Notification
							</Button>
						</div>
					</Col>
				</Row>
			</div>

			<div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
				<label style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--mg-text-secondary)" }}>Wheels this sample can see</label>
				<span style={{ fontSize: 11, color: "var(--mg-text-disabled)" }}>
					{assignablePanels.length} of {panelList.length} assignable
				</span>
				<Input
					allowClear
					size="small"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					prefix={<SearchOutlined />}
					placeholder="Search wheels"
					aria-label="Search wheels"
					style={{ marginLeft: "auto", width: 180 }}
				/>
				<Button size="small" onClick={handleSelectAll}>{allAssignableSelected ? "Deselect All" : "Select All"}</Button>
			</div>

			<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 8 }}>
				{visiblePanels.map((panel) => {
					const assignable = isAssignable(panel);
					const checked = assignable && selectedPanels.includes(panel.id_panels);
					// Total on purpose: a status this build has never seen renders as published instead of throwing.
					const tag = STATUS_TAG[panelStatusOf(panel)] ?? STATUS_TAG.published;
					const statusTag = <StatusTag status={panelStatusOf(panel)} label={tag.label} color={tag.color} size="small" />;
					const meta = panelMeta(panel);

					return (
						<div
							key={panel.id_panels}
							data-testid="gene-codes-wheel-row"
							style={{
								display: "flex",
								alignItems: "center",
								gap: 12,
								padding: "10px 12px",
								border: `1px solid ${checked ? "var(--mg-primary-border)" : "var(--mg-border)"}`,
								borderRadius: "var(--mg-radius-md)",
								background: checked ? "var(--mg-primary-bg)" : "var(--mg-bg-card)",
								opacity: assignable ? 1 : 0.5,
							}}
						>
							<Checkbox checked={checked} disabled={!assignable} aria-label={panel.name_panel} onChange={(e) => handleCheckboxChange(panel.id_panels, e.target.checked)} />
							{renderMiniWheel(panel)}
							<div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
								<span style={{ fontSize: 12, fontWeight: 600, color: "var(--mg-text)" }}>{panel.name_panel}</span>
								{meta ? <span style={{ fontSize: 10, color: "var(--mg-text-secondary)" }}>{meta}</span> : null}
							</div>
							<div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
								{tag.tooltip ? (
									<Tooltip title={tag.tooltip}>
										<span data-testid="gene-codes-wheel-status">{statusTag}</span>
									</Tooltip>
								) : (
									statusTag
								)}
								{assignable ? null : <span style={{ fontSize: 10, color: "var(--mg-text-disabled)" }}>Not assignable</span>}
							</div>
						</div>
					);
				})}
			</div>

			{unassignableIds.size > 0 ? (
				<div style={{ display: "flex", gap: 8, padding: "10px 12px", borderRadius: "var(--mg-radius-md)", background: "var(--mg-warning-bg)", border: "1px solid var(--mg-warning)" }}>
					<WarningOutlined style={{ color: "var(--mg-warning)", marginTop: 2 }} />
					<span style={{ fontSize: 11, lineHeight: 1.5, color: "var(--mg-text)" }}>
						Only a published or hidden wheel can be handed to a patient. A draft or archived wheel stays in the list so an author can see it exists, and the assignment is refused server-side as well — this checkbox is not the gate.
					</span>
				</div>
			) : null}
		</div>
	);
};

export default GeneCodesTab;
